import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RichTextAttachmentHelper } from '../../../../services/helpers/rich-text-attachment.helper';
import { ImageLightboxService } from '../../../../services/helpers/image-lightbox.service';
import { normalizeBlankLinesForDisplay, sanitizeRichTextHtml } from '../../../../services/helpers/rich-text-content.helper';

@Component({
  selector: 'app-edit-panel-description',
  templateUrl: './edit-panel-description.component.html',
  styleUrls: ['./edit-panel-description.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class EditPanelDescriptionComponent implements OnChanges {
  @Input() description = '';
  @Input() descriptionDraft = '';
  @Input() isEditing = false;
  @Input() formData: { [key: string]: any } = {};
  @Input() projectId = '';
  @Input() workspaceId = '';
  @Input() nodeInstanceId = '';
  @Input() nodeKey = '';

  @Output() descriptionDraftChange = new EventEmitter<string>();
  @Output() startEdit = new EventEmitter<void>();
  @Output() saveEdit = new EventEmitter<void>();

  // Read-only display copy of `description` with attachment-id references resolved
  // back to real images. Marked as trusted so Angular's [innerHTML] sanitizer doesn't
  // strip the data-attachment-id attribute the image-hover/lightbox styling and click
  // handling depend on.
  public resolvedDescription: SafeHtml | string = '';

  private lastResolvedDescriptionSource: string | null = null;

  constructor(
    private richTextAttachmentHelper: RichTextAttachmentHelper,
    private imageLightboxService: ImageLightboxService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['description']) {
      this.loadDisplayDescription(this.description ?? '');
    }
  }

  // The display view only ever shows a thumbnail (see loadDisplayDescription); clicking
  // it normally enters edit mode, but clicking a tagged image instead opens the shared
  // lightbox to view it at full resolution.
  public onDisplayClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const attachmentId = target?.closest?.('img[data-attachment-id]')?.getAttribute('data-attachment-id');
    if (attachmentId) {
      event.stopPropagation();
      this.imageLightboxService.open(attachmentId);
      return;
    }
    this.startEdit.emit();
  }

  public onDraftChange(value: string): void {
    this.descriptionDraftChange.emit(value);
  }

  // RichTextFieldComponent already withholds this while its attachment picker is open (see
  // its onEditorBlur/setPickerOpen), so a real blur here always means the user is genuinely
  // done editing — safe to save and collapse back to display mode.
  public onEditorBlur(value: string): void {
    this.descriptionDraftChange.emit(value);
    this.saveEdit.emit();
  }

  private loadDisplayDescription(incoming: string): void {
    // This value is user-authored and comes straight from the database, so it is scrubbed
    // before it reaches [innerHTML] — it can carry markup written outside Quill (API import,
    // ET sync, legacy rows). The scrub happens first and once: resolveEmbeddedImages parses
    // the HTML into a live DOM, so anything script-bearing has to be gone before it runs.
    // bypassSecurityTrustHtml is still needed afterwards because Angular's own sanitizer
    // would strip data-attachment-id — see sanitizeRichTextHtml for why that is fatal here.
    // A blank line pasted from Word arrives as a space-only paragraph, which has no height
    // once whitespace collapses here (the editor's white-space: pre-wrap hid that).
    const safeHtml = normalizeBlankLinesForDisplay(sanitizeRichTextHtml(incoming));
    this.resolvedDescription = this.sanitizer.bypassSecurityTrustHtml(safeHtml);
    this.lastResolvedDescriptionSource = incoming;
    this.richTextAttachmentHelper.resolveEmbeddedImages(safeHtml).subscribe({
      next: (resolved) => {
        if (this.lastResolvedDescriptionSource === incoming) {
          this.resolvedDescription = this.sanitizer.bypassSecurityTrustHtml(resolved);
          // This component is OnPush — the thumbnail fetch resolves on its own async
          // timer (HTTP round-trip), not from an @Input change or template event, so
          // without this the resolved image would sit unpainted until something else
          // triggered change detection.
          this.cdr.markForCheck();
        }
      },
      // resolveEmbeddedImages forkJoins one fetch per image, so a single failed thumbnail
      // errors the whole stream. Keep the already-rendered scrubbed HTML (placeholders and
      // all) rather than letting the rejection surface as an unhandled error.
      error: (err) => console.error('Failed to resolve embedded images in description', err),
    });
  }
}
